// skipcq: JS-W1028, JS-0128
import React from 'react';
import { Link } from 'react-router-dom';
import {
  CardContent,
  CardActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import useSystemStats from '../../hooks/useSystemStats';
import useBreweryTrend from '../../hooks/useBreweryTrend';
import DashboardSurfaceCard from './DashboardSurfaceCard';
import Sparkline from '../widgets/Sparkline';

function BreweryManagementCard() {
  const { systemStats, loading, error } = useSystemStats();
  const { trendData, loading: trendLoading } = useBreweryTrend();

  // Extract brewery counts from metrics
  const breweriesCount = systemStats?.metrics?.breweriesCount || 0;
  const openBreweriesCount = systemStats?.metrics?.openBreweriesCount || 0;

  // Extract trend values for sparklines
  const trendValues = trendData.map((item) => item.breweriesCount);
  const openTrendValues = trendData.map((item) => item.openBreweriesCount);

  // Helper function to calculate change metrics
  const calculateChange = (values) => {
    const firstValue = values[0] || 0;
    const lastValue = values[values.length - 1] || 0;
    const countChange = lastValue - firstValue;
    const percentChange = firstValue > 0 ? (countChange / firstValue) * 100 : 0;
    return { countChange, percentChange };
  };

  // Helper function to get trend icon
  const getTrendIcon = (change) => {
    if (change > 0) return TrendingUp;
    if (change < 0) return TrendingDown;
    return TrendingFlat;
  };

  // Helper function to get trend color
  const getTrendColor = (change) => {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  };

  // Calculate changes for total breweries
  const totalBreweryChange = calculateChange(trendValues);
  const TotalTrendIcon = getTrendIcon(totalBreweryChange.countChange);

  // Calculate changes for open breweries
  const openBreweryChange = calculateChange(openTrendValues);
  const OpenTrendIcon = getTrendIcon(openBreweryChange.countChange);

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Brewery Management
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Manage brewery data, locations, and information
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            Error loading stats
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
              <Typography
                variant="h4"
                component="div"
                color="primary"
                sx={{ mr: 2 }}
              >
                {breweriesCount.toLocaleString()}
              </Typography>
              {!trendLoading && trendValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={trendValues}
                      width="100%"
                      height={24}
                      color="primary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <TotalTrendIcon
                      sx={{
                        color: getTrendColor(totalBreweryChange.countChange),
                        fontSize: 16,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(totalBreweryChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {totalBreweryChange.countChange > 0 ? '+' : ''}
                        {totalBreweryChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(totalBreweryChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({totalBreweryChange.percentChange > 0 ? '+' : ''}
                        {totalBreweryChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Total breweries
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mt: 1
              }}>
              <Typography
                variant="h5"
                component="div"
                color="secondary"
                sx={{ mr: 2 }}
              >
                {openBreweriesCount.toLocaleString()}
              </Typography>
              {!trendLoading && openTrendValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={openTrendValues}
                      width="100%"
                      height={20}
                      color="secondary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <OpenTrendIcon
                      sx={{
                        color: getTrendColor(openBreweryChange.countChange),
                        fontSize: 14,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: getTrendColor(openBreweryChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {openBreweryChange.countChange > 0 ? '+' : ''}
                        {openBreweryChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: getTrendColor(openBreweryChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({openBreweryChange.percentChange > 0 ? '+' : ''}
                        {openBreweryChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Open breweries
            </Typography>
          </>
        )}
      </CardContent>
      <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Button variant="outlined" component={Link} to="/breweries" fullWidth>
          Manage Breweries
        </Button>
        <Button variant="text" component={Link} to="/brewery-groups" fullWidth size="small">
          Brewery groups
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default BreweryManagementCard;
